<template>
  <div data-view="google-manager">
    <Can
      action="view"
      subject="ad-manager.order"
    >
      <AddNewOrderDialog
        v-if="isAddNewOrderModalOn"
        :is-on="isAddNewOrderModalOn"
        :is-loading="isAddingOrder"
        :on-cancel="toggleNewOrderModal"
        :on-confirm="createOrder"
        :open-on-create="selectOrder"
      />
    </Can>
    <Can
      action="view-business-contact"
      subject="ad-manager.order"
    >
      <dialog-empty :is-on="isAddNewOrderStepperOn" :on-close="toggleNewOrderStepper">
        <template v-slot:content>
          <AddNewOrderStepper
            v-if="isAddNewOrderStepperOn"
            ref="promostepper"
            :promoted-deal="{}"
            :flow="promotionFlow"
            :on-cancel="toggleNewOrderStepper"
            :on-confirm="createOrder"
            :on-update="updateOrder"
            :on-create-li="createLineItem"
            :select-on-create="selectOrder"
          />
        </template>
      </dialog-empty>
    </Can>

    <EditOrderDialog
      v-if="isEditOrderModalOn"
      :is-on="isEditOrderModalOn"
      :is-business="isBusiness"
      :order="selectedOrder"
      :is-editing-order="isEditingOrder"
      :can-edit-details="canEditDetails"
      :on-cancel="toggleEditOrderModal"
      :on-confirm="updateOrder"
      :get-orders="getFilteredOrders"
      :is-loading-order="isLoading"
      :on-toggle="toggleEditDetails"
      :on-change-status="updateStatus"
    />

    <layout-logged-frame title="Promotions" has-before>
      <template v-slot:header-actions>
        <Can
          action="view"
          subject="ad-manager.order"
        >
          <v-btn
            color="primary"
            @click="toggleNewOrderModal"
          >
            <v-icon>add</v-icon>
            {{ $t('ad_manager_add_new_title') }}
          </v-btn>
        </Can>

        <Can
          action="view-business-contact"
          subject="ad-manager.order"
        >
          <v-btn
            id="new-promotion"
            color="primary"
            :disabled="!hasEnabledForPromotionCountries"
            @click="openNewOrderStepper"
          >
            <v-icon>add</v-icon>
            {{ $t('new_promotion') }}
          </v-btn>
        </Can>
      </template>

      <template v-slot:before>
        <NavigationTabs :tabs="navigationTabs"/>
      </template>

      <template v-slot:content>
        <PromotionsAlerts
          v-if="isBusiness"
          :incomplete="incompleteBilling"
          :pending="pendingBilling"
          :denied="deniedBilling"
        />

        <!-- only markets we run Ad Manager for; if the list is empty the platform select falls back to user/countries -->
        <PromotionsFilters
          ref="ordersfilter"
          :is-skeleton-loading="isSkeletonLoading"
          :countries="adManagerCountries"
          @filter="onFilter"
          @clearFilters="onClearFilters"
        />

        <table-base
          :columns="isBusiness ? columnsBC : columns"
          :items="orders"
          :pages="totalPages"
          :pagination="pagination"
          :is-loading="isLoading"
          :can-edit-columns="false"
          :on-select-item="selectOrder"
          :on-change-page="onChangePage"
          :on-update-pagination="onUpdatePagination"
          :sync-to-url="true"
          @init-pagination="onInitPagination"
          has-custom-items-template
        >
          <template
            v-slot:item-cell="scope"
            
          >
            <template v-if="scope.cell.column.value === 'isArchived'">
              {{ displayArchivedColumn(scope.cell.item) }}
            </template>

            <template v-else-if="scope.cell.column.value === 'lines'">
              <LineItemStatus
                :items="scope.cell.item"
                :item-name="scope.cell.item.name"
                :expanded="expandedRows"
              />
            </template>

            <template v-else-if="scope.cell.column.value === 'impressions'">
              <LineItemImpressions
                :items="scope.cell.item"
                :item-name="scope.cell.item.name"
                :expanded="expandedRows"
                :on-toggle="onToggleRow"
              />
            </template>

            <template v-else>
              {{ displayColumnValue(scope.cell.item, scope.cell.column.value) }}
            </template>
          </template>
        </table-base>
      </template>
    </layout-logged-frame>
  </div>
</template>

<script>
import PromotionsFilters from '../../components/forms/filters/Promotions';
import urlPaginationRestore from '@/mixins/urlPaginationRestore';
import AddNewOrderDialog from '../../components/dialog/order/AddNew';
import AddNewOrderStepper from '../../components/steppers/Promotion';
import EditOrderDialog from '../../components/dialog/order/Edit';
import LineItemStatus from '../../components/panels/lineItems/Status';
import LineItemImpressions from '../../components/panels/lineItems/Impressions';
import PromotionsAlerts from '../../components/alerts/Promotions';
import columns from '../../config/promotions/orderColumns';
import columnsBC from '../../config/promotions/orderColumnsBC';
import navigationTabs from '../../config/promotions/navigationTabs';
import NavigationTabs from '../../components/NavigationTabs'
import {extractNestedProp, isNum, isObj, isStr} from '@/utils';

export default {
  mixins: [urlPaginationRestore],
    components: {
      PromotionsFilters,
      AddNewOrderDialog,
      EditOrderDialog,
      AddNewOrderStepper,
      LineItemStatus,
      LineItemImpressions,
      PromotionsAlerts,
      NavigationTabs,
    },
    data() {
      return {
        navigationTabs,
        columns,
        columnsBC,
        totalPages: 0,
        pagination: {
          order: 'desc' ,
          page: 1,
          rowsPerPage: 15,
          sortBy: '',
          totalItems: 0,
          search: ''
        },
        query: { page: 1, archived: 1, per_page: 15 },
        selectedOrder: {},
        createdOrder: {},
        expandedRows: [],
        urlOrderId: null,
        promotionFlow: '',
        isSkeletonLoading: true,
        isLoading: false,
        isAddNewOrderModalOn: false,
        isEditOrderModalOn: false,
        isAddNewOrderStepperOn: false,
        isAddingOrder: false,
        isEditingOrder: false,
        canEditDetails: false
      };
    },
    computed: {
      disabledForPromotionCountries() {
        const { disabledForPromotionCountries } = this.$store.getters['businesses/activeItem'] || {};
        return disabledForPromotionCountries || [];
      },
      hasEnabledForPromotionCountries() {
        return this.disabledForPromotionCountries.length !== this.enabledPromoCountries.length;
      },
      activeUser() {
        return this.$store.getters['auth/user'];
      },
      enabledPromoCountries() {
        return this.$store.getters['businessUsers/enabledPromoCountries'];
      },
      userCountries() {
        return this.$store.getters['users/countries'];
      },
      adManagerCountries() {
        // An internal user manages markets we do not run Ad Manager for (SE, US). Those have no
        // trafficker id, so filtering the promotions by them cannot return anything and the API
        // rejects it - do not offer them. A business user's enabled list is GAM-scoped already.
        if (this.enabledPromoCountries.length > 0) {
          return this.enabledPromoCountries;
        }

        return this.userCountries.filter((country) => country.ad_manager);
      },
      orders() {
        return this.$store.getters['promotions/items'];
      },
      billingCountriesByStatus() {
        const { billingCountriesByStatus } = this.$store.getters['businesses/activeItem'];
        return billingCountriesByStatus || [];
      },
      totalCount() {
        return this.pagination.totalItems || 0;
      },
      canUpdateOrder() {
        return this.$ability.can('update', 'ad-manager.order') || this.activeUser.business_id;
      },
      isBusiness() {
        return this.$ability.can('view-business-contact', 'ad-manager.order');
      },
      incompleteBilling() {
        return this.alertMessageBaseCondition('no_billing');
      },
      pendingBilling() {
        return this.alertMessageBaseCondition('networks_in_review');
      },
      deniedBilling() {
        return this.alertMessageBaseCondition('networks_denied');
      },
    },
    created() {
      const { $route, getOrders, query } = this;

      if ($route.params.id) {
        this.urlOrderId = Number($route.params.id);
      }

      // Wrapped like the other two triggers: this view fetches in its own
      // created() as well, so a deep link had three initial fetches racing and
      // the unfiltered page 1 went out first.
      this.runInitialFetch(() => getOrders(this.query)
        .then(res => {
          this.preSelectOrder(res);
          return res;
        })
        .catch(error => error));
    },
    methods: {
      getOrders(query = {}) {
        this.isLoading = true;

        return this.$store.dispatch('promotions/getOrders', query)
          .then(res => {
            const { itemsMeta } = this.$store.getters['promotions/itemsData'];
            const { total, current_page, last_page } = itemsMeta;

            this.pagination.page = current_page;
            this.pagination.totalItems = total;
            this.totalPages = last_page;
            this.isSkeletonLoading = false;
            this.isLoading = false;

            return res;
          })
          .catch((err) => {
            this.isSkeletonLoading = false;
            this.isLoading = false;

            return err;
          });
      },
      getFilteredOrders() {
        if (this.$refs.ordersfilter) {
          this.$refs.ordersfilter.onFilter();
        }
      },
      onFilter(filters) {
        this.query = {
          ...this.query,
          search: filters.search,
          date_filter: filters.date_filter,
          advertiser_id: filters.advertiser_id ?? null,
          created_by: filters.created_by ?? null,
          order_type: filters.order_type ?? -1,
          countries: filters.countries,
          page: 1,
        };

        this.runInitialFetch(() => this.getOrders(this.query));
      },
      onClearFilters() {
        const { sortBy } = this.pagination;
        this.query = { page: 1, sort: sortBy, archived: 1, per_page: 15 };

        this.getOrders(this.query);
      },
      toggleNewOrderModal() {
        this.isAddNewOrderModalOn = !this.isAddNewOrderModalOn;
      },
      toggleNewOrderStepper() {
        if (this.isAddNewOrderStepperOn && this.$refs.promostepper && this.$refs.promostepper.$refs.addnewlikb) {
          this.$refs.promostepper.$refs.addnewlikb.clear();
        }

        this.isAddNewOrderStepperOn = !this.isAddNewOrderStepperOn;
      },
      openNewOrderStepper(event) {
        if (isObj(event) && isStr(event.currentTarget.id)) {
          this.promotionFlow = event.currentTarget.id;
        }

        this.toggleNewOrderStepper();
      },
      displayColumnValue(item, columnValue) {
        return extractNestedProp(item, columnValue);
      },
      displayArchivedColumn(item) {
        return item.isArchived ? 'Yes' : 'No';
      },
      createLineItem(lineItem) {
        return this.$store.dispatch('promotions/createLineItem', lineItem)
          .then(res => {
            this.getOrders();
            return res;
          });
      },
      createOrder(order) {
        this.isAddingOrder = true;

        return this.$store.dispatch('promotions/createOrder', order)
          .then(res => {
            this.isAddingOrder = false;

            if (res.success) {
              const { order, sortBy } = this.pagination;
              const nextQuery = {
                ...this.query,
                order,
                sort: sortBy
              };

              this.getOrders(nextQuery);

              this.isAddNewOrderModalOn = false;
              this.createdOrder = res.data.data.data;
            }

            return res;
          });
      },
      async updateOrder(order) {
        if (!this.canUpdateOrder) {
          return undefined;
        }

        const nextOrder = {
          ...order,
          id: this.selectedOrder.id || this.createdOrder.id,
          isBC: this.isBusiness
        };

        this.isEditingOrder = true;

        return this.$store.dispatch('promotions/updateOrder', nextOrder)
          .then(res => {
            this.isEditOrderModalOn = false;

            this.getFilteredOrders();

            return res;
          })
          .catch((err) => err)
          .finally(() => {
            this.isEditingOrder = false;
          });
      },
      updateStatus(status) {
        if (!this.canUpdateOrder) {
          return undefined;
        }

        const nextOrder = {
          ...status,
          id: this.selectedOrder.id,
          statusId: status.id,
          isBC: this.isBusiness
        };

        this.isEditingOrder = true;

        return this.$store.dispatch('promotions/updateStatus', nextOrder)
          .then(() => {
            const { sortBy } = this.pagination;
            const nextQuery = {
              ...this.query,
              sort: sortBy
            };

            this.isEditingOrder = false;
            this.isEditOrderModalOn = false;

            return this.getOrders(nextQuery);
          })
          .catch((err) => {
            this.isEditingOrder = false;
            return err;
          });
      },
      selectOrder(order) {
        this.selectedOrder = order;
        this.isEditOrderModalOn = true;

        if (this.isBusiness && isObj(order) && isObj(order.business) && isObj(order.advertiser)) {
          this.getBusinessPlacements(order.business.id, order.advertiser.id, order.country?.code);
        }

        return this.$store.dispatch('promotions/setActiveOrder', order);
      },
      preSelectOrder(res) {
        if (this.urlOrderId && res.items) {
          const activeOrder = res.items.filter(e => e.id === this.urlOrderId).shift();

          if (isObj(activeOrder) && isNum(activeOrder.id)) {
            this.selectOrder(activeOrder);
          }
        }
      },
      getBusinessPlacements(business_id, advertiser_id, country) {
        const query = {
          business_id,
          advertiser_id,
          country
        };

        return this.$store.dispatch('businesses/getBusinessPlacements', query)
          .then(res => res.placements)
          .catch(err => err);
      },
      toggleEditOrderModal() {
        if (!this.isAddNewOrderModalOn) {
          this.canEditDetails = false;
        }
        this.isEditOrderModalOn = !this.isEditOrderModalOn;
      },
      onChangePage(page) {
        const { query, pagination } = this;
        const { order, sortBy } = pagination;
        const nextQuery = {
          ...query,
          page,
          sort: sortBy,
          order,
        };
        this.query = nextQuery;

        this.getOrders(nextQuery);
      },
      onUpdatePagination({ page, order, sortBy }) {
        if (!sortBy) return null;

        const { query, pagination } = this;
        this.pagination.order = order;
        this.pagination.sortBy = isStr(sortBy) ? sortBy : pagination.sortBy;

        const nextQuery = {
          ...query,
          page,
          sort: this.pagination.sortBy,
          order,
        };
        this.query = nextQuery;

        this.runInitialFetch(() => this.getOrders(nextQuery));
      },
      onToggleRow(name) {
        const itemIndex = this.expandedRows.findIndex(i => i === name);

        if (itemIndex > -1) {
          this.expandedRows.splice(itemIndex, 1);
        } else {
          this.expandedRows.push(name);
        }
      },
      toggleEditDetails() {
        this.canEditDetails = !this.canEditDetails;
      },
      alertMessageBaseCondition(arrayName) {
        const { billingCountriesByStatus } = this;

        // eslint-disable-next-line max-len
        if ( billingCountriesByStatus && billingCountriesByStatus[arrayName] && billingCountriesByStatus[arrayName].length) {
          return billingCountriesByStatus[arrayName];
        }

        return [];
      },
    }
  };
</script>

<style lang="scss">

</style>
